import React, { memo } from 'react';
import styled, { css } from 'styled-components';

// Design tokens for consistent styling
export const cardVariants = {
  elevated: css`
    box-shadow: var(--shadow-lg);
    border: 1px solid var(--color-gray-100);
  `,
  flat: css`
    box-shadow: var(--shadow-sm);
    border: 1px solid var(--color-gray-200);
  `,
  outlined: css`
    box-shadow: none;
    border: 2px solid var(--color-gray-300);
  `,
  ghost: css`
    box-shadow: none;
    border: 1px solid transparent;
    background: transparent;
  `
} as const;

export const cardSizes = {
  sm: css`
    padding: 1rem;
    border-radius: var(--border-radius-md);
  `,
  md: css`
    padding: 1.5rem;
    border-radius: var(--border-radius-lg);
  `,
  lg: css`
    padding: 2rem;
    border-radius: var(--border-radius-xl);
  `
} as const;

interface CardProps {
  children: React.ReactNode;
  variant?: keyof typeof cardVariants;
  size?: keyof typeof cardSizes;
  className?: string;
  onClick?: () => void;
  role?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

const StyledCard = styled.div<{
  $variant: keyof typeof cardVariants;
  $size: keyof typeof cardSizes;
  $clickable: boolean;
}>`
  background: var(--color-white);
  transition: all 0.2s ease-in-out;
  
  ${({ $variant }) => cardVariants[$variant]}
  ${({ $size }) => cardSizes[$size]}
  
  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-xl);
      }
      
      &:active {
        transform: translateY(-1px);
      }
    `}
`;

/**
 * Reusable Card component following design system patterns
 * 
 * @example
 * <Card variant="elevated" size="md" onClick={handleClick}>
 *   <h3>Card Title</h3>
 *   <p>Card content</p>
 * </Card>
 */
export const Card = memo<CardProps>(({
  children,
  variant = 'flat',
  size = 'md',
  className,
  onClick,
  role,
  'aria-label': ariaLabel,
  'data-testid': testId
}) => {
  return (
    <StyledCard
      className={className}
      $variant={variant}
      $size={size}
      $clickable={!!onClick}
      onClick={onClick}
      role={role}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {children}
    </StyledCard>
  );
});

Card.displayName = 'Card';